/**
 * Client: Fiesta Sessions list
 * Access: Authenticated + Active Membership
 */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CardMedia,
  Button,
  Chip,
  Paper,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Event as EventIcon,
  Link as LinkIcon,
  OndemandVideo as OndemandVideoIcon,
  EventNote as EventNoteIcon,
  Lock as LockIcon,
  NotificationsActive as BellIcon,
  Schedule as ScheduleIcon,
  Language as LanguageIcon,
  PlayCircle as PlayCircleIcon,
} from "@mui/icons-material";
import { formatInTimeZone } from "date-fns-tz";
import DOMPurify from "dompurify";
import { hasMembershipPrefix } from "@smfa/utils/src/store";
import CalendarAdd from "@smfa/utils/src/components/CalendarAdd";

import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";
import VideoEmbed from "../components/Common/VideoEmbed";
import UpsellOrLogin from "../components/Common/UpsellOrLogin";
import EmptyState from "../components/Common/EmptyState";
import { grey } from "@mui/material/colors";

// Event configuration
const EVENT_CONFIG = {
  timezone: "America/New_York", // Default TZ for events
  timezoneName: "Eastern Time", // Human-readable TZ name
  defaultDuration: 60, // Default duration (mins)
};

/**
 * Fiesta Sessions Page
 * @returns {JSX.element}
 * @component
 */
const FiestaSession = () => {
  const theme = useTheme();
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [previousSessions, setPreviousSessions] = useState([]);
  const [upsellDialogOpen, setUpsellDialogOpen] = useState(false);
  const [upsellFeature, setUpsellFeature] = useState("content");
  const [timingDialogOpen, setTimingDialogOpen] = useState(false);
  const [timingDialogData, setTimingDialogData] = useState({
    url: "",
    message: "",
    session: null,
  });
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoDialogSession, setVideoDialogSession] = useState(null);

  const auth = useSelector((state) => state.auth);
  const { data: userData = {} } = useSelector((state) => state.user);
  const { about } = useSelector((state) => state.pageContents || {});
  const { fiesta = {} } = about || {};

  const isAuthenticated = auth?.isAuthenticated || false;
  const requireMembership = true;
  const hasMembership = !requireMembership
    ? true
    : hasMembershipPrefix(userData); // No prefix, accept any membership
  //const hasMembership = false; // For testing
  // Format session date with TZ info
  const formatSessionDate = (dateString) => {
    if (!dateString) {
      return "";
    }

    try {
      const date = new Date(dateString);

      // Format: "Monday, Jan 1, 2024 • 2:30 PM (ET)"
      return (
        formatInTimeZone(
          date,
          EVENT_CONFIG.timezone,
          "EEEE, MMM d, yyyy • h:mm a",
        ) + ` (${EVENT_CONFIG.timezoneName.substring(0, 2)})`
      );
    } catch (e) {
      log.error("Error formatting date:", e);
      return dateString;
    }
  };

  // Sort and categorize sessions
  useEffect(() => {
    if (!isEmpty(fiesta)) {
      const allSessions = [...fiesta.sessions];

      // Sort sessions by date
      allSessions.sort(
        (a, b) => new Date(a.session_dt) - new Date(b.session_dt),
      );

      const now = new Date();

      // Split into upcoming and previous sessions
      const upcoming = allSessions.filter(
        (session) => new Date(session.session_dt) >= now,
      );

      const previous = allSessions
        .filter((session) => new Date(session.session_dt) < now)
        .reverse(); // Reverse to show most recent first

      // Formatted dates to sessions
      const processedUpcoming = upcoming.map((session) => ({
        ...session,
        session_dt_display: formatSessionDate(session.session_dt),
      }));

      const processedPrevious = previous.map((session) => ({
        ...session,
        session_dt_display: formatSessionDate(session.session_dt),
      }));

      setUpcomingSessions(processedUpcoming);
      setPreviousSessions(processedPrevious);
    }
  }, [fiesta]);

  // Handle protected content clicks
  const handleProtectedClick = (feature) => {
    if (!isAuthenticated || !hasMembership) {
      setUpsellFeature(feature);
      setUpsellDialogOpen(true);
    }
  };

  // Check if current time is within session window (1 hour before to 6 hours after)
  const checkSessionTiming = (sessionDate) => {
    const now = new Date();
    const sessionTime = new Date(sessionDate);
    const oneHourBefore = new Date(sessionTime.getTime() - 60 * 60 * 1000);
    const sixHoursAfter = new Date(sessionTime.getTime() + 6 * 60 * 60 * 1000);

    if (now < oneHourBefore) {
      return {
        inWindow: false,
        message:
          "The session has not started yet, continue to Zoom link anyway?",
      };
    } else if (now > sixHoursAfter) {
      return {
        inWindow: false,
        message: "The session is over, continue to Zoom link anyway?",
      };
    }

    return { inWindow: true, message: "" };
  };

  // Handle session URL click with timing check
  const handleSessionUrlClick = (session, url, sessionIsUpcoming = false) => {
    if (!sessionIsUpcoming) {
      // For previous sessions, go directly to URL
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    const timingCheck = checkSessionTiming(session.session_dt);
    if (timingCheck.inWindow) {
      // Within window, go directly
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      // Outside window, show dialog
      setTimingDialogData({
        url: url,
        message: timingCheck.message,
        session: session,
      });
      setTimingDialogOpen(true);
    }
  };

  // Render session cards with proper access control
  const renderSessionCard = (session, isUpcoming = false, isNext = false) => {
    // Determine if this is the next upcoming session (first in the list)
    const isNextSession = isUpcoming && isNext;
    const isPrevious = !isUpcoming;
    const hasArchiveVideo =
      session.session_video_url && session.session_video_url.trim() !== "";
    const shouldGreyOut = isPrevious && !hasArchiveVideo;

    return (
      <Grid size={{ xs: 12, md: 6 }} key={session.session_id}>
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            maxWidth: "400px",
            boxShadow: isNextSession ? 8 : isPrevious ? 2 : 3,
            borderRadius: 2,
            overflow: "hidden",
            position: "relative",
            transition: "transform 0.2s, box-shadow 0.2s, opacity 0.2s",
            "&:hover": {
              transform: isNextSession
                ? "translateY(-4px)"
                : isPrevious
                  ? "translateY(-2px)"
                  : "translateY(-4px)",
              boxShadow: isNextSession ? 6 : isPrevious ? 4 : 6,
            },
            ...(isNextSession && {
              borderTop: `4px solid ${theme.palette.primary.main}`,
            }),
            ...(isPrevious && {
              borderLeft: `3px solid ${theme.palette.secondary.main}`,
              opacity: 0.95,
            }),
          }}
        >
          {/* "Next Session" badge */}
          {isNextSession && (
            <Chip
              label="Next Session"
              color="primary"
              icon={<BellIcon fontSize="small" />}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 1,
                fontWeight: "bold",
              }}
            />
          )}

          {/* Card Media or Placeholder */}
          {session.session_image_display ? (
            <Box sx={{ position: "relative" }}>
              <CardMedia
                component="img"
                height="200"
                image={session.session_image_display}
                alt={session.session_title}
                sx={{
                  objectFit: "cover",
                  ...(isNextSession && {
                    filter: "brightness(1.05)",
                  }),
                  ...(isPrevious && {
                    filter: "grayscale(0.2) brightness(0.9)",
                  }),
                }}
              />
              {/* Archive Chip for previous sessions - bottom overlay with shadow */}
              {isPrevious && (
                <Chip
                  label={hasArchiveVideo ? "Archive" : "No Video"}
                  color={hasArchiveVideo ? "secondary" : "default"}
                  variant="filled"
                  icon={<OndemandVideoIcon fontSize="small" />}
                  sx={{
                    position: "absolute",
                    right: 16,
                    bottom: 16,
                    zIndex: 2,
                    fontSize: "0.85rem",
                    height: "28px",
                    minWidth: 90,
                    boxShadow: 3,
                    borderRadius: 2,
                    backgroundColor: hasArchiveVideo
                      ? theme.palette.secondary.main
                      : theme.palette.action.disabled,
                    color: hasArchiveVideo
                      ? theme.palette.secondary.contrastText
                      : theme.palette.text.disabled,
                    cursor: hasArchiveVideo ? "pointer" : "default",
                    transition: "box-shadow 0.2s, background 0.2s",
                    ...(shouldGreyOut && {
                      opacity: 0.7,
                    }),
                  }}
                  onClick={
                    hasArchiveVideo
                      ? () => {
                          setVideoDialogSession(session);
                          setVideoDialogOpen(true);
                        }
                      : undefined
                  }
                  clickable={!!hasArchiveVideo}
                />
              )}
            </Box>
          ) : (
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  height: 100,
                  bgcolor: isNextSession
                    ? "primary.light"
                    : isPrevious
                      ? "secondary.light"
                      : "action.selected",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  ...(isPrevious && {
                    opacity: 0.8,
                  }),
                }}
              >
                {isPrevious && (
                  <OndemandVideoIcon
                    sx={{
                      fontSize: 40,
                      color: "secondary.main",
                      opacity: 0.6,
                    }}
                  />
                )}
              </Box>
              {/* Archive Chip for previous sessions - bottom overlay with shadow */}
              {isPrevious && (
                <Chip
                  label={hasArchiveVideo ? "Archive" : "No Video"}
                  color={hasArchiveVideo ? "secondary" : "default"}
                  variant="filled"
                  icon={<OndemandVideoIcon fontSize="small" />}
                  sx={{
                    position: "absolute",
                    right: 8,
                    bottom: 8,
                    zIndex: 2,
                    fontSize: "0.85rem",
                    height: "28px",
                    minWidth: 90,
                    boxShadow: 3,
                    borderRadius: 2,
                    backgroundColor: hasArchiveVideo
                      ? theme.palette.secondary.main
                      : theme.palette.action.disabled,
                    color: hasArchiveVideo
                      ? theme.palette.secondary.contrastText
                      : theme.palette.text.disabled,
                    cursor: hasArchiveVideo ? "pointer" : "default",
                    transition: "box-shadow 0.2s, background 0.2s",
                    ...(shouldGreyOut && {
                      opacity: 0.7,
                    }),
                  }}
                  onClick={
                    hasArchiveVideo
                      ? () => {
                          setVideoDialogSession(session);
                          setVideoDialogOpen(true);
                        }
                      : undefined
                  }
                  clickable={!!hasArchiveVideo}
                />
              )}
            </Box>
          )}

          <CardContent
            sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
          >
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                ...(isNextSession && {
                  color: "primary.main",
                  fontWeight: "bold",
                }),
                ...(isPrevious && {
                  color: "text.primary",
                  fontWeight: "500",
                  fontSize: "1.25rem", // Slightly smaller than upcoming
                }),
              }}
            >
              {session.session_title}
            </Typography>

            {/* Date & Time */}
            {session.session_dt_display && (
              <Box
                display="flex"
                alignItems="center"
                mb={1.5}
                color={
                  isNextSession
                    ? "primary.main"
                    : isPrevious
                      ? "secondary.main"
                      : "text.secondary"
                }
              >
                <ScheduleIcon sx={{ mr: 1 }} fontSize="small" />
                <Tooltip
                  title={`Event time shown in ${EVENT_CONFIG.timezoneName}`}
                  arrow
                  placement="top"
                >
                  <Box display="flex" alignItems="center">
                    <Typography
                      variant="body2"
                      sx={{
                        ...(isPrevious && {
                          fontWeight: "500",
                        }),
                      }}
                    >
                      {session.session_dt_display}
                    </Typography>
                    <LanguageIcon
                      sx={{ ml: 0.5, opacity: 0.7 }}
                      fontSize="inherit"
                    />
                  </Box>
                </Tooltip>
              </Box>
            )}

            {/* Session Content */}
            {session.session_content && (
              <Typography
                variant="body1"
                component="div"
                sx={{
                  my: 2,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: isPrevious ? 3 : 4, // Show less content for previous sessions
                  textOverflow: "ellipsis",
                  ...(isPrevious && {
                    color: "text.secondary",
                    fontSize: "0.9rem",
                  }),
                }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(session.session_content),
                  }}
                />
              </Typography>
            )}

            {/* Session URL */}
            {session.session_url && (
              <Box display="flex" alignItems="center" mb={1.5}>
                <LinkIcon sx={{ mr: 1 }} fontSize="small" color="primary" />
                {isAuthenticated && hasMembership ? (
                  <Button
                    variant="text"
                    color={!isPrevious ? "primary" : grey[500]}
                    size="small"
                    onClick={
                      !isPrevious
                        ? () =>
                            handleSessionUrlClick(
                              session,
                              session.session_url,
                              isUpcoming,
                            )
                        : () => alert("This meeting link has expired.")
                    }
                    sx={{
                      p: 0,
                      minWidth: "auto",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      ...(isPrevious && {
                        color: "text.secondary",
                        "&:hover": {
                          color: "primary.main",
                        },
                      }),
                    }}
                  >
                    {!isPrevious ? (
                      "Join Session / View Details"
                    ) : (
                      <s>{session.session_url}</s>
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={() => handleProtectedClick("Fiesta Session Link")}
                    startIcon={<LockIcon fontSize="small" />}
                    sx={{
                      ...(isPrevious && {
                        color: "text.secondary",
                        "&:hover": {
                          color: "primary.main",
                        },
                      }),
                    }}
                  >
                    {isPrevious
                      ? "Members Only: View Details"
                      : "Members Only: View Meeting Link"}
                  </Button>
                )}
              </Box>
            )}

            {/* Add to Calendar (only for upcoming events) */}
            {isUpcoming && (
              <Box sx={{ mt: 2, alignSelf: "flex-start" }}>
                <CalendarAdd
                  event={{
                    title: session.session_title,
                    description: session.session_content,
                    startDate: session.session_dt,
                    location: session.session_url,
                    duration: EVENT_CONFIG.defaultDuration,
                    id: session.session_id,
                  }}
                  requireAuth={true}
                  isAuthenticated={isAuthenticated && hasMembership}
                  onAuthRequired={handleProtectedClick}
                  calendarConfig={EVENT_CONFIG}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderEmptyState = (message) => (
    <EmptyState
      icon={<EventIcon sx={{ fontSize: 48, opacity: 0.7 }} />}
      title="No Sessions Found"
      description={message}
    />
  );

  return (
    <PageLayout pageName="Fiesta Sessions">
      <PageBanner
        bannerImage={fiesta?.banner?.banner_image}
        mobileBannerImage={fiesta?.banner?.banner_image_mob_display}
        mainTitle={fiesta?.banner?.banner_title || "Fiesta Sessions"}
        pageTitle="Fiesta Sessions"
      />

      <Container sx={{ py: 5 }}>
        {/* Introduction */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 5,
            borderRadius: 2,
            bgcolor: "background.paper",
            textAlign: "center",
          }}
        >
          {fiesta?.intro?.section_title && (
            <Typography variant="h4" component="h1" gutterBottom>
              {fiesta.intro.section_title}
            </Typography>
          )}

          {fiesta?.intro?.section_description && (
            <Box sx={{ maxWidth: 800, mx: "auto" }}>
              <Typography
                variant="body1"
                component="div"
                color="text.secondary"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(fiesta.intro.section_description),
                }}
              />
            </Box>
          )}

          {/* Timezone info */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              mt: 2,
              bgcolor: "action.hover",
              px: 2,
              py: 1,
              borderRadius: 2,
              color: "text.secondary",
            }}
          >
            <LanguageIcon sx={{ mr: 1, fontSize: 18 }} />
            <Typography variant="body2">
              All times shown in {EVENT_CONFIG.timezoneName}
            </Typography>
          </Box>
        </Paper>

        {/* Upcoming Sessions Section */}
        <Box sx={{ mb: 6 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              pb: 1,
            }}
          >
            <EventNoteIcon color="primary" sx={{ mr: 1.5 }} />
            <Typography variant="h5" component="h2" fontWeight="bold">
              Upcoming Sessions
            </Typography>
          </Box>

          {upcomingSessions.length > 0 ? (
            <Grid container spacing={3}>
              {upcomingSessions.map((session, index) =>
                renderSessionCard(session, true, index === 0),
              )}
            </Grid>
          ) : (
            renderEmptyState(
              "There are no upcoming sessions scheduled at this time. Please check back later for new events.",
            )
          )}
        </Box>

        {/* Previous Sessions Section */}
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              borderBottom: `2px solid ${theme.palette.secondary.main}`,
              pb: 1,
            }}
          >
            <OndemandVideoIcon color="secondary" sx={{ mr: 1.5 }} />
            <Typography variant="h5" component="h2" fontWeight="bold">
              Previous Sessions
            </Typography>
          </Box>

          {previousSessions.length > 0 ? (
            <Grid container spacing={3}>
              {previousSessions.map((session) =>
                renderSessionCard(session, false),
              )}
            </Grid>
          ) : (
            renderEmptyState(
              "No previous sessions are available in our archives yet.",
            )
          )}
        </Box>
      </Container>

      {/* Upsell Dialog */}
      <UpsellOrLogin
        open={upsellDialogOpen}
        onClose={() => setUpsellDialogOpen(false)}
        featureName={upsellFeature}
        isAuthenticated={isAuthenticated}
        requireMembership={true}
        hasMembership={hasMembership}
      />

      {/* Session Timing Warning Dialog */}
      <Dialog
        open={timingDialogOpen}
        onClose={() => setTimingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Session Timing Warning</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{timingDialogData.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimingDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              window.open(
                timingDialogData.url,
                "_blank",
                "noopener,noreferrer",
              );
              setTimingDialogOpen(false);
            }}
            color="primary"
            variant="contained"
          >
            Continue to Zoom
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Archive Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={() => setVideoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {videoDialogSession?.session_title || "Session Archive Video"}
        </DialogTitle>
        <DialogContent dividers>
          {videoDialogSession && (
            <>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {videoDialogSession.session_dt_display}
              </Typography>
              <Typography
                variant="body1"
                component="div"
                sx={{ mb: 3 }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    videoDialogSession.session_content || "",
                  ),
                }}
              />
              {isAuthenticated && hasMembership ? (
                <VideoEmbed
                  urlOrEmbed={videoDialogSession.session_video_url}
                  title={videoDialogSession.session_title}
                />
              ) : (
                <Box
                  sx={{ my: 3, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Button
                    startIcon={<PlayCircleIcon />}
                    variant="outlined"
                    color="success"
                    onClick={() => {
                      setUpsellFeature("Fiesta Session Archives");
                      setUpsellDialogOpen(true);
                    }}
                  >
                    Fiesta Session Archive videos are a members-only feature.
                    Sign up or log in to watch.
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVideoDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default FiestaSession;
